import React, { useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import _classNames from 'classnames';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Mixpanel from 'utils/mixpanelService';
import { getUserFirstName, replacePlaceholders } from 'utils/stringUtils';
import { useSelector } from 'react-redux';
import { makeSelectProfile } from 'containers/Auth/selectors';
import CourseSavedResources from './CourseSavedResources';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(3),
  },
  widgetsWrapper: {
    marginBottom: theme.spacing(3),
  },
  widget: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  progress: {
    marginBottom: 8,
    position: 'relative',
    color: '#37BB7C',
  },
  progressBar: ({ size, thickness }) => ({
    borderRadius: '50%',
    boxShadow: `inset 0 0 0 ${(thickness / 44) * size}px #E4FFF3`,
  }),
  progressText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    ...theme.typography.h4,
    color: theme.palette.text.primary,
    width: '100%',
    textAlign: 'center',
  },
  subTitle: {
    ...theme.typography.pRegular,
    color: theme.palette.grey[600],
    textAlign: 'center',
    fontSize: props => (props.isMobile ? '0.875rem' : '1rem'),
  },
  modulesHeader: {
    color: '#EA6810',
    marginRight: 2,
  },
  modulesSubHeader: {
    color: '#A6A6A6',
    ...theme.typography.h7,
  },
  reflectedHeader: {
    color: theme.palette.primary.secondary,
    marginBottom: 16,
  },
  completedModulesHeader: {
    color: `${theme.palette.text.primary}!important`,
  },
  continueHeader: {
    color: theme.palette.primary.main,
  },
  continueSection: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.grey[300]}`,
    display: 'flex',
    flexDirection: props => props.isMobile && 'column',
    alignItems: props => (props.isMobile ? 'flex-start' : 'center'),
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  continueSubTitle: {
    ...theme.typography.pRegular,
    color: theme.palette.grey[600],
  },
  continueButton: {
    ...theme.typography.button,
    color: theme.palette.text.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
    padding: props => (props.isMobile ? '6px 8px' : '10px 16px'),
    marginTop: props => props.isMobile && theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  congratulationsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 68px 20px',
    background: 'linear-gradient(80.57deg, #D2F4FD -23.74%, #DBE2F7 122.1%)',
    marginBottom: theme.spacing(3),
  },
}));

const progressSize = 100;
const progressThickness = 2;

const CourseDashboard = ({
  isMobile,
  continueCourse,
  coursesSiteCopy,
  totalNumberOfModules,
  completedNumberOfModules,
  courseProgress,
  completedNumberOfReflections,
  takenCourse,
  course,
  isCourseCompleted,
  removeResourceFromLater,
  completedModulesIds,
  getNextResourceIndexes,
  goToResource,
  onModuleClick,
  trackingProps,
  prevPath,
  headingVariant,
}) => {
  const classes = useStyles({
    size: progressSize,
    thickness: progressThickness,
    isMobile,
  });

  useEffect(() => {
    Mixpanel.track('Course Dashboard - Viewed', {
      course: course.fields.title,
      path: prevPath,
    });
  }, []);

  // We need to sort the completed modules by their order in the course
  const sortedCompletedModules = useMemo(
    () =>
      _get(course, 'fields.modules', []).filter(m =>
        completedModulesIds.includes(m.sys.id),
      ),
    [completedModulesIds, course],
  );

  const lastActiveModule = useMemo(() => {
    const { moduleIdx } = getNextResourceIndexes();
    const courseModules = _get(course, 'fields.modules', []);

    return _get(courseModules, [moduleIdx]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  const profile = useSelector(makeSelectProfile());
  const userName = getUserFirstName(profile);

  const handleNavigateToModule = module => {
    const moduleIdx = _get(course, 'fields.modules', []).findIndex(
      m => m.sys.id === module.sys.id,
    );

    onModuleClick(moduleIdx);

    const courseModule = _get(course, ['fields', 'modules', moduleIdx]);
    Mixpanel.track('Course Dashboard - Module View - Clicked', {
      course: course.fields.title,
      module: _get(courseModule, 'fields.title'),
    });
  };

  const getSectionWidget = ({
    title,
    subTitle,
    onClick,
    buttonText,
    customHeaderClassName,
  }) => (
    <Box className={classes.continueSection}>
      <Box mr={2}>
        <Typography className={classes.continueSubTitle}>{subTitle}</Typography>
        <Typography
          variant="subtitle2"
          className={_classNames(classes.continueHeader, customHeaderClassName)}
        >
          {title}
        </Typography>
      </Box>
      <Button
        className={classes.continueButton}
        endIcon={<ChevronRightIcon />}
        onClick={onClick}
      >
        {buttonText}
      </Button>
    </Box>
  );

  return (
    <Box>
      {!isCourseCompleted && (
        <Typography
          variant={headingVariant}
          color="textPrimary"
          className={classes.title}
        >
          {replacePlaceholders(
            _get(coursesSiteCopy, 'pageCopy.keepGoingUser'),
            {
              '#name': userName,
            },
          )}
        </Typography>
      )}
      {isCourseCompleted ? (
        <Box className={classes.congratulationsContainer}>
          <Typography variant={headingVariant} color="primary">
            {_get(coursesSiteCopy, 'pageCopy.congratulationsTitle')}
          </Typography>
          <Typography color="primary">
            {replacePlaceholders(
              _get(coursesSiteCopy, 'pageCopy.congratulationsMessage'),
              {
                '#courseTitle': course.fields.title,
              },
            )}
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          direction="row"
          spacing={2}
          className={classes.widgetsWrapper}
        >
          <Grid item xs={12} sm={4}>
            <Box className={classes.widget}>
              <Box className={classes.progress}>
                <CircularProgress
                  variant="determinate"
                  size={progressSize}
                  thickness={progressThickness}
                  color="inherit"
                  value={courseProgress}
                  className={classes.progressBar}
                />
                <Typography className={classes.progressText}>
                  {courseProgress}%
                </Typography>
              </Box>
              <Typography className={classes.subTitle}>
                {_get(coursesSiteCopy, 'pageCopy.overallProgress')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Box className={classes.widget}>
              <Box display="flex" alignItems="baseline" mb={2}>
                <Typography variant="h1" className={classes.modulesHeader}>
                  {completedNumberOfModules}
                </Typography>
                <Typography className={classes.modulesSubHeader}>
                  / {totalNumberOfModules}
                </Typography>
              </Box>
              <Typography className={classes.subTitle}>
                {_get(coursesSiteCopy, 'pageCopy.modulesProgress')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Box className={classes.widget}>
              <Typography variant="h1" className={classes.reflectedHeader}>
                {completedNumberOfReflections}
              </Typography>
              <Typography className={classes.subTitle}>
                {_get(coursesSiteCopy, 'pageCopy.reflectionsProgress')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
      {!isCourseCompleted && (
        <Box>
          <Typography
            variant={headingVariant}
            color="textPrimary"
            className={classes.title}
          >
            {_get(coursesSiteCopy, 'pageCopy.continueWhereLeftOff')}
          </Typography>
          {getSectionWidget({
            title: _get(lastActiveModule, 'fields.title'),
            subTitle: _get(coursesSiteCopy, 'pageCopy.continueLearning'),
            onClick: () => continueCourse(lastActiveModule),
            buttonText: _get(coursesSiteCopy, 'pageCopy.continue'),
          })}
        </Box>
      )}
      {!!sortedCompletedModules.length && (
        <Box>
          <Typography
            variant={headingVariant}
            color="textPrimary"
            className={classes.title}
          >
            {_get(coursesSiteCopy, 'pageCopy.modulesCompleted')}
          </Typography>
          <Box>
            {sortedCompletedModules.map(module => (
              <Box key={_get(module, 'sys.id')}>
                {getSectionWidget({
                  title: _get(module, 'fields.title'),
                  subTitle: _get(coursesSiteCopy, 'pageCopy.review'),
                  onClick: () => handleNavigateToModule(module),
                  buttonText: _get(coursesSiteCopy, 'pageCopy.view'),
                  customHeaderClassName: classes.completedModulesHeader,
                })}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <CourseSavedResources
        course={course}
        takenCourse={takenCourse}
        removeResourceFromLater={removeResourceFromLater}
        goToResource={goToResource}
        trackingProps={trackingProps}
        headingVariant={headingVariant}
        small={isMobile}
      />
    </Box>
  );
};

export default CourseDashboard;
